import { NetworkStatus } from "@apollo/client";
import { Checkbox, Skeleton } from "@fullscript/aviary-web";
import type { ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, ShowMoreButton, Spacer, Typography } from "@aviary";
import { useBrandsQuery } from "@shared/data/queries/BrandsQuery/Brands.query";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { l } from "@shared/locales/i18n";
import type * as Types from "@shared/types/graphqlGenerated";

import type { Brand as BrandFilter } from "../../../CatalogSearch";

import * as styles from "./AllBrands.styles";

type Brand = Pick<Types.BrandContextualFilters, "id" | "esResultCount"> & {
  brand: Pick<Types.Brand, "id" | "name" | "logo">;
};
type NonContextualBrand = Pick<Types.Brand, "id" | "name" | "logo">;

interface Props {
  isBrandActive: (id: String) => boolean;
  onBrandSelect: (brand: BrandFilter) => void;
  isContextual?: boolean;
  contextualData?: Brand[];
}

const AllBrands = ({
  isBrandActive,
  onBrandSelect,
  isContextual = false,
  contextualData,
}: Props) => {
  const { t } = useTranslation();
  const { activeBrands } = useCatalogSearch();

  const [showAll, setShowAll] = useState(activeBrands.length > 0);

  const { data, loading, fetchMore, networkStatus } = useBrandsQuery({
    variables: {
      filters: { active: true },
    },
    skip: isContextual,
    notifyOnNetworkStatusChange: true,
  });

  // new contextual filters that include result count
  const brandsData: Brand[] = contextualData ?? [];

  const renderCheckboxLabel = (brandWithCount: Brand) => {
    return `${brandWithCount.brand.name} (${brandWithCount.esResultCount})`;
  };

  const renderBrandCheckboxes = (): ReactNode[] => {
    return brandsData.map((brandWithCount: Brand) => (
      <div css={styles.checkboxContainer} key={brandWithCount.brand.id}>
        <Checkbox
          id={brandWithCount.brand.id}
          type="checkbox"
          checked={isBrandActive(brandWithCount.brand.id)}
          onChange={() =>
            onBrandSelect({
              id: brandWithCount.brand.id,
              name: brandWithCount.brand.name,
              logo: brandWithCount.brand?.logo ?? "",
            })
          }
          isFullWidth
        >
          {renderCheckboxLabel(brandWithCount)}
        </Checkbox>
      </div>
    ));
  };

  const renderAllBrands = (): ReactNode => {
    if (showAll) {
      return <>{renderBrandCheckboxes()}</>;
    }
  };

  // old filters that don't include result count
  const viewerBrands = data?.viewer?.brands;
  const nonContextualBrandsData: NonContextualBrand[] = viewerBrands?.nodes ?? [];
  const hasNextPage = viewerBrands?.pageInfo?.hasNextPage;

  const renderNonContextualCheckboxLabel = (brand: NonContextualBrand) => {
    return brand.name;
  };

  const renderNonContextualBrandCheckboxes = (): ReactNode[] => {
    return nonContextualBrandsData.map((brand: NonContextualBrand) => (
      <div css={styles.checkboxContainer} key={brand.id}>
        <Checkbox
          id={brand.id}
          type="checkbox"
          checked={isBrandActive(brand.id)}
          onChange={() =>
            onBrandSelect({ id: brand.id, name: brand.name, logo: brand?.logo ?? "" })
          }
          isFullWidth
        >
          {renderNonContextualCheckboxLabel(brand)}
        </Checkbox>
      </div>
    ));
  };

  const fetchMoreBrands = () =>
    fetchMore({
      variables: {
        after: viewerBrands?.pageInfo?.endCursor,
        filters: {
          active: true,
        },
      },
      updateQuery: (prevResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prevResult;

        return {
          ...prevResult,
          viewer: {
            ...prevResult.viewer,
            brands: {
              ...fetchMoreResult.viewer.brands,
              nodes: [...prevResult.viewer.brands.nodes, ...fetchMoreResult.viewer.brands.nodes],
              pageInfo: {
                ...fetchMoreResult.viewer.brands.pageInfo,
              },
            },
          },
        };
      },
    });

  const renderLoadMoreButton = () => {
    if (hasNextPage) {
      return (
        <div css={styles.loadMoreBrandsButton}>
          <Button intention="system" onClick={fetchMoreBrands} isLoading={loading} size="xsmall">
            {t(l.sharedCatalog.filter.LoadMoreBrands)}
          </Button>
        </div>
      );
    }

    return null;
  };

  const renderSkeleton = (index: number) => <Skeleton type="typography" isFullWidth key={index} />;

  const renderSkeletons = (numOfSkeletons: number): ReactNode[] =>
    Array.from({ length: numOfSkeletons }).map((_, i) => renderSkeleton(i));

  const renderNonContextualAllBrands = (): ReactNode => {
    if (showAll) {
      const initialLoading = loading && networkStatus !== NetworkStatus.fetchMore;
      if (initialLoading) {
        return <span>{renderSkeletons(25)}</span>;
      }

      return (
        <>
          {renderNonContextualBrandCheckboxes()}
          {renderLoadMoreButton()}
        </>
      );
    }
  };

  // shared logic for both contextual and non-contextual filters
  if (isContextual && (contextualData?.length ?? 0) <= 5) {
    return null;
  }

  const renderTopShowMoreButton = () => {
    if (showAll) {
      return <ShowMoreButton onClick={() => setShowAll(!showAll)} isExpanded={showAll} />;
    }

    return null;
  };

  return (
    <>
      <Spacer height="half" />
      {renderTopShowMoreButton()}
      <Typography css={[!showAll ? styles.hidden : undefined, styles.header]} type="h5">
        {t(l.sharedCatalog.filter.AllBrands)}
      </Typography>
      <div css={showAll ? styles.container : undefined}>
        {isContextual ? renderAllBrands() : renderNonContextualAllBrands()}
      </div>
      <Spacer height="half" />
      <ShowMoreButton
        data-e2e="all-brands-show-more-button"
        onClick={() => setShowAll(!showAll)}
        isExpanded={showAll}
      />
    </>
  );
};

export { AllBrands };
